// Real User Tracking Library
// Captures actual user behavior, browser fingerprinting, and interactions

import { API_BASE } from './api';

class RealUserTracker {
  private sessionId: string;
  private apiBase: string;
  private fingerprint: any = {};
  private isTracking: boolean = false;
  private interactionBuffer: any[] = [];

  constructor(apiBase: string = API_BASE) {
    this.apiBase = apiBase;
    this.sessionId = this.generateSessionId();
    this.isTracking = true;
    
    console.log('🔍 Real User Tracker initialized - Session:', this.sessionId.slice(-8));
    
    this.initializeTracking();
  }

  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private async initializeTracking() {
    // Collect browser fingerprint
    await this.collectBrowserFingerprint();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Track initial page load
    this.trackPageView();
    
    // Send fingerprint data
    this.sendFingerprint();
    
    // Start interaction buffer flush
    this.startInteractionBufferFlush();
  }

  private async collectBrowserFingerprint() {
    try {
      this.fingerprint = {
        // Screen and display information
        screenResolution: `${screen.width}x${screen.height}`,
        screenColorDepth: screen.colorDepth,
        screenPixelDepth: screen.pixelDepth,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        
        // Browser and system information
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        languages: navigator.languages,
        cookiesEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        
        // Timezone and locale
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        
        // Hardware information
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: (navigator as any).deviceMemory,
        
        // Browser features
        localStorage: typeof Storage !== 'undefined',
        sessionStorage: typeof sessionStorage !== 'undefined',
        indexedDB: typeof indexedDB !== 'undefined',
        webGL: this.getWebGLFingerprint(),
        canvas: await this.getCanvasFingerprint(),
        
        // Network information
        connection: (navigator as any).connection ? {
          effectiveType: (navigator as any).connection.effectiveType,
          downlink: (navigator as any).connection.downlink,
          rtt: (navigator as any).connection.rtt
        } : null,
        
        // Additional browser capabilities
        plugins: this.getPluginsList(),
        mimeTypes: this.getMimeTypesList(),
        
        // Timestamp
        collectedAt: new Date().toISOString()
      };
    } catch (error) {
      console.warn('Error collecting browser fingerprint:', error);
    }
  }

  private getWebGLFingerprint(): string | null {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
      if (!gl) return null;
      
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return null;
      
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      
      return `${vendor} - ${renderer}`;
    } catch (error) {
      return null;
    }
  }

  private async getCanvasFingerprint(): Promise<string | null> {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Real User Tracking 🔍', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Fingerprint Test', 4, 45);
      
      return canvas.toDataURL();
    } catch (error) {
      return null;
    }
  }

  private getPluginsList(): string[] {
    try {
      return Array.from(navigator.plugins).map(plugin => plugin.name);
    } catch (error) {
      return [];
    }
  }

  private getMimeTypesList(): string[] {
    try {
      return Array.from(navigator.mimeTypes).map(mimeType => mimeType.type);
    } catch (error) {
      return [];
    }
  }

  private setupEventListeners() {
    // Page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.trackInteraction('visibility_change', {
        hidden: document.hidden
      });
    });

    // Mouse movements (throttled)
    let lastMouseMove = 0;
    document.addEventListener('mousemove', (event) => {
      const now = Date.now();
      if (now - lastMouseMove > 1000) { // Throttle to once per second
        this.trackInteraction('mousemove', {
          x: event.clientX,
          y: event.clientY,
          target: this.getElementInfo(event.target as Element)
        });
        lastMouseMove = now;
      }
    });

    // Clicks
    document.addEventListener('click', (event) => {
      this.trackInteraction('click', {
        x: event.clientX,
        y: event.clientY,
        target: this.getElementInfo(event.target as Element),
        button: event.button
      });
    });

    // Scrolling (throttled)
    let lastScroll = 0;
    document.addEventListener('scroll', () => {
      const now = Date.now();
      if (now - lastScroll > 2000) { // Throttle to once per 2 seconds
        this.trackInteraction('scroll', {
          scrollY: window.scrollY,
          scrollX: window.scrollX,
          scrollHeight: document.documentElement.scrollHeight,
          clientHeight: window.innerHeight
        });
        lastScroll = now;
      }
    });

    // Key presses (without capturing actual keys for privacy)
    document.addEventListener('keydown', (event) => {
      this.trackInteraction('keypress', {
        key: event.key.length === 1 ? 'char' : event.key, // Don't capture actual characters
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        shiftKey: event.shiftKey
      });
    });

    // Form interactions
    document.addEventListener('focus', (event) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        this.trackInteraction('form_focus', {
          target: this.getElementInfo(event.target),
          inputType: event.target.type
        });
      }
    }, true);

    // Page unload
    window.addEventListener('beforeunload', () => {
      this.flushInteractionBuffer();
    });

    // Resize events
    window.addEventListener('resize', () => {
      this.trackInteraction('resize', {
        viewport: `${window.innerWidth}x${window.innerHeight}`
      });
    });
  }

  private getElementInfo(element: Element | null): any {
    if (!element) return null;
    
    return {
      tagName: element.tagName,
      id: element.id || null,
      className: element.className || null,
      textContent: element.textContent?.substring(0, 50) || null // First 50 chars only
    };
  }

  private trackInteraction(type: string, data: any) {
    if (!this.isTracking) return;
    
    const interaction = {
      type,
      timestamp: new Date().toISOString(),
      data,
      sessionId: this.sessionId
    };

    this.interactionBuffer.push(interaction);

    // Flush buffer if it gets too large
    if (this.interactionBuffer.length > 20) {
      this.flushInteractionBuffer();
    }
  }

  private startInteractionBufferFlush() {
    // Flush interactions every 10 seconds
    setInterval(() => {
      if (this.interactionBuffer.length > 0) {
        this.flushInteractionBuffer();
      }
    }, 10000);
  }

  private async flushInteractionBuffer() {
    if (this.interactionBuffer.length === 0) return;

    const interactions = [...this.interactionBuffer];
    this.interactionBuffer = [];

    try {
      // Send interactions in batch
      for (const interaction of interactions) {
        await fetch(`${this.apiBase}/api/tracking/interaction`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sessionId: this.sessionId,
            type: interaction.type,
            element: interaction.data.target,
            coordinates: interaction.data.x && interaction.data.y ? {
              x: interaction.data.x,
              y: interaction.data.y
            } : null,
            timestamp: interaction.timestamp,
            additionalData: interaction.data
          })
        });
      }
    } catch (error) {
      console.warn('Error sending interactions:', error);
      // Put interactions back in buffer to retry later
      this.interactionBuffer.unshift(...interactions);
    }
  }

  public async trackPageView(url?: string, title?: string) {
    if (!this.isTracking) return;

    const pageData = {
      url: url || window.location.href,
      title: title || document.title,
      referrer: document.referrer,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      fingerprint: this.fingerprint
    };

    try {
      const response = await fetch(`${this.apiBase}/api/tracking/pageview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pageData)
      });

      const result = await response.json();
      
      if (result.success && result.botAnalysis) {
        console.log(`📊 Page tracked: ${pageData.url} | Bot Risk: ${result.botAnalysis.riskLevel} (${Math.round(result.botAnalysis.botProbability * 100)}%)`);
      }
    } catch (error) {
      console.warn('Error tracking page view:', error);
    }
  }

  private async sendFingerprint() {
    try {
      await fetch(`${this.apiBase}/api/tracking/fingerprint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          fingerprint: this.fingerprint
        })
      });
    } catch (error) {
      console.warn('Error sending fingerprint:', error);
    }
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public getFingerprint(): any {
    return { ...this.fingerprint };
  }

  public stopTracking() {
    this.isTracking = false;
    this.flushInteractionBuffer();
    console.log('🛑 Real user tracking stopped');
  }

  public resumeTracking() {
    this.isTracking = true;
    console.log('▶️ Real user tracking resumed');
  }
}

// Global tracker instance
let globalTracker: RealUserTracker | null = null;

// Initialize tracking
export const initializeRealTracking = (apiBase?: string): RealUserTracker => {
  if (!globalTracker) {
    globalTracker = new RealUserTracker(apiBase);
  }
  return globalTracker;
};

// Get current tracker
export const getRealTracker = (): RealUserTracker | null => {
  return globalTracker;
};

// Track page view manually
export const trackPageView = (url?: string, title?: string) => {
  if (globalTracker) {
    globalTracker.trackPageView(url, title);
  }
};

export default RealUserTracker;
